import { useEffect, useState } from 'react';

/**
 * Custom hook to detect media query matches
 * @param query - CSS media query string (e.g., "(max-width: 768px)")
 * @returns boolean indicating if the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    // Initialize with current match state
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia(query);

    // Update if initial state was different
    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    media.addEventListener('change', listener);

    return () => {
      media.removeEventListener('change', listener);
    };
  }, [query, matches]);

  return matches;
};

/**
 * Breakpoint constants matching the design system
 */
export const BREAKPOINTS = {
  mobile: '(max-width: 480px)',
  tablet: '(max-width: 768px)',
  laptop: '(max-width: 1024px)',
  desktop: '(min-width: 1025px)',
} as const;

/**
 * Custom hook for common breakpoint detection
 * @returns object with boolean flags for each breakpoint
 */
export const useBreakpoint = () => {
  const isMobile = useMediaQuery(BREAKPOINTS.mobile);
  const isTablet = useMediaQuery(BREAKPOINTS.tablet);
  const isLaptop = useMediaQuery(BREAKPOINTS.laptop);
  const isDesktop = useMediaQuery(BREAKPOINTS.desktop);

  return {
    isMobile,
    isTablet,
    isLaptop,
    isDesktop,
    // Convenience flags
    isMobileOrTablet: isMobile || isTablet,
    isTabletOrLaptop: isTablet || isLaptop,
  };
};
